import { createSelector } from '@reduxjs/toolkit';
import { BonusOrderBy } from 'pages/bonus/partials/BonusCardForm/BonusCardForm.config';
import {
  makeSelectAuthApiConfig,
  makeSelectResourceLoadingState,
} from 'services/api/api.selectors';
import { RequestApiDataIfNecessaryPayload } from 'services/api/api.types';
import {
  getBonusFreebetDetailEndpoint,
  getBonusSpendabilityEndpoint,
  getFunBonusDetailApiConfig,
  getFunBonusList,
  getGameBonusEndpoint,
  getNewBonusListEndpoint,
  useCouponEndpoint,
} from 'services/bonus/bonus.config';
import {
  BonusSpendability,
  GameBonus,
  GameBonusDetail,
  GameBonusFunDetail,
  GameBonusServiceResponse,
  GameBonusWagerDetail,
  getBonusSpendabilityResponseSchema,
} from 'services/bonus/bonus.types';
import { selectSessionInfo } from 'services/session/session.selectors';
import { AppState, ReduxSelector } from 'store/types';
import { v4 as uuidV4 } from 'uuid';
import { PromoCodeRequestPayload } from './bonus.types';

export function selectGetBonusSpendabilityApiConfig(
  reduxState: AppState,
): RequestApiDataIfNecessaryPayload {
  return {
    resourceId: getBonusSpendabilityEndpoint,
    requestConfig: makeSelectAuthApiConfig({
      url: getBonusSpendabilityEndpoint,
      method: 'GET',
    })(reduxState),
    skipDefaultServerErrorHandling: true,
    successSchemaValidator: getBonusSpendabilityResponseSchema,
  };
}

export function makeSelectGetGameBonusDetailEndpointApiConfig({
  idBonus,
}: {
  idBonus: number;
}): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectGetBonusWagerDetailEndpointApiConfig(
    reduxState: AppState,
  ): RequestApiDataIfNecessaryPayload {
    const {
      ipAddress,
      channel,
      channelInfo: infoChannel,
    } = selectSessionInfo(reduxState);

    return {
      resourceId: getFunBonusDetailApiConfig,
      requestConfig: makeSelectAuthApiConfig({
        url: `${getFunBonusDetailApiConfig}${idBonus}`,
        method: 'GET',
        timeout: 25000,
        params: {
          infoChannel,
          ipAddress,
          channel,
          requestId: uuidV4(),
          idBonus: idBonus,
        },
      })(reduxState),
      invalidateIfPresent: true,
    };
  };
}

export function makeSelectCreateSubmitPromoCodeApiConfig(
  requestPayload: PromoCodeRequestPayload,
): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectSubmitPromoCodeApiConfig(
    reduxState: AppState,
  ): RequestApiDataIfNecessaryPayload {
    const url = useCouponEndpoint;

    const requestConfig = makeSelectAuthApiConfig({
      url,
      method: 'POST',
      data: requestPayload,
    })(reduxState);

    return {
      resourceId: url,
      requestConfig,
      invalidateIfPresent: true,
      skipDefaultServerErrorHandling: true,
    };
  };
}

export function makeSelectGetFunBonusListEndpointApiConfig({
  status,
  bonusType = 'DEFAULT',
  orderBy,
  page,
  size = 7,
}: {
  status: string;
  bonusType?: string;
  orderBy: BonusOrderBy | '-1';
  page?: number;
  size?: number;
}): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectGetBonusListEndpointApiConfig(
    reduxState: AppState,
  ): RequestApiDataIfNecessaryPayload {
    const params = {
      requestId: uuidV4(),
      status,
      bonusType,
      orderBy,
      page,
      size,
    };

    return {
      resourceId: getFunBonusList,
      requestConfig: makeSelectAuthApiConfig({
        url: getFunBonusList,
        method: 'GET',
        params,
      })(reduxState),
      invalidateIfPresent: true,
      skipDefaultServerErrorHandling: true,
    };
  };
}

export function makeSelectGetBonusListEndpointApiConfig({
  status,
  bonusType = 'DEFAULT',
  orderBy,
  page,
  size = 7,
}: {
  status: string;
  bonusType?: string;
  orderBy: BonusOrderBy | '-1';
  page?: number;
  size?: number;
}): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectGetBonusListEndpointApiConfig(
    reduxState: AppState,
  ): RequestApiDataIfNecessaryPayload {
    const params = {
      requestId: uuidV4(),
      status,
      bonusType,
      orderBy,
      page,
      size,
    };

    return {
      resourceId: getNewBonusListEndpoint,
      requestConfig: makeSelectAuthApiConfig({
        url: getNewBonusListEndpoint,
        method: 'GET',
        params,
      })(reduxState),
      invalidateIfPresent: true,
      skipDefaultServerErrorHandling: true,
    };
  };
}

export function makeSelectGetBonusWagerDetailEndpointApiConfig({
  bonusId,
}: {
  bonusId: number;
}): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectGetBonusWagerDetailEndpointApiConfig(
    reduxState: AppState,
  ): RequestApiDataIfNecessaryPayload {
    return {
      resourceId: getBonusFreebetDetailEndpoint,
      requestConfig: makeSelectAuthApiConfig({
        url: `${getBonusFreebetDetailEndpoint}${bonusId}`,
        method: 'GET',
        params: {
          requestId: uuidV4(),
          bonusId,
        },
      })(reduxState),
      invalidateIfPresent: true,
      skipDefaultServerErrorHandling: true,
    };
  };
}

export function makeSelectGetBonusFreebetDetailEndpointApiConfig({
  bonusId,
}: {
  bonusId: number;
}): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectGetBonusFreebetDetailEndpointApiConfig(
    reduxState: AppState,
  ): RequestApiDataIfNecessaryPayload {
    return {
      resourceId: getBonusFreebetDetailEndpoint,
      requestConfig: makeSelectAuthApiConfig({
        url: `${getBonusFreebetDetailEndpoint}${bonusId}`,
        method: 'GET',
        params: {
          requestId: uuidV4(),
          bonusId,
        },
      })(reduxState),
      invalidateIfPresent: true,
      skipDefaultServerErrorHandling: true,
    };
  };
}

export const selectBonusState = (reduxState: AppState) => reduxState.bonus;

export const selectGameBonus: ReduxSelector<GameBonus[]> = (
  reduxState: AppState,
) => selectBonusState(reduxState).gameBonus;

export const selectGameBonusDetail: ReduxSelector<GameBonusDetail[]> = (
  reduxState: AppState,
) => selectBonusState(reduxState).gameBonusDetail;

export const selectBonusList: ReduxSelector<GameBonusServiceResponse[]> = (
  reduxState: AppState,
) => selectBonusState(reduxState).bonusList;

interface BonusState {
  bonusList: GameBonusServiceResponse[];
  totalSize: number;
}

export const makeSelectBonusList = (queryParams: string) => {
  return createSelector(
    [(state: AppState) => state.bonus],
    (bonus: BonusState) => {
      return {
        bonusList: bonus.bonusList.filter(
          (bonusItem) => bonusItem.bonusType === queryParams,
        ),
        totalSize: bonus.totalSize,
      };
    },
  );
};

export const selectBonusWagerDetail: ReduxSelector<
  GameBonusWagerDetail | null
> = (reduxState: AppState) => selectBonusState(reduxState).bonusWagerDetail;

export const selectBonusFunDetail: ReduxSelector<GameBonusFunDetail | null> = (
  reduxState: AppState,
) => selectBonusState(reduxState).bonusFunDetail;

export const selectGameBonusLoadState =
  makeSelectResourceLoadingState(getGameBonusEndpoint);

export const selectBonusSpendabilityLoadState = makeSelectResourceLoadingState(
  getBonusSpendabilityEndpoint,
);

export const selectBonusSpendability: ReduxSelector<BonusSpendability[]> = (
  state: AppState,
) => state.bonus.spendability ?? [];

export const selectGameBonusDetailLoadState = makeSelectResourceLoadingState(
  getFunBonusDetailApiConfig,
);

export const selectBonusListLoadState = makeSelectResourceLoadingState(
  getNewBonusListEndpoint,
);

export const selectBonusListFunLoadState =
  makeSelectResourceLoadingState(getFunBonusList);

export const selectBonusWagerDetailLoadState = makeSelectResourceLoadingState(
  getBonusFreebetDetailEndpoint,
);

export const selectBonusFreebetDetailLoadState = makeSelectResourceLoadingState(
  getBonusFreebetDetailEndpoint,
);
